import useTranslation from 'next-translate/useTranslation';

import styles from './HeadshotLanding.module.css';

type Plan = {
  title: string;
  price: string;
  features: string[];
  priceID: string;
  buttonText: string;
};
type Testimonial = {
  name: string;
  title: string;
  quote: string;
  stars: number;
};
interface StudioPricingProps {
  plans: Plan[];
  testimonials: Testimonial[];
  pricingType: 'individual' | 'team';
  setPricingType: (type: 'individual' | 'team') => void;
  teamSize: number;
  setTeamSize: (size: number) => void;
  discount: number;
  discountedPrice: number;
  originalPrice: number;
  pendingPrice: string | null;
  onPurchase: (price: string, members?: number) => void;
}

export default function StudioPricing({
  plans,
  testimonials,
  pricingType,
  setPricingType,
  teamSize,
  setTeamSize,
  discount,
  discountedPrice,
  originalPrice,
  pendingPrice,
  onPurchase,
}: StudioPricingProps) {
  const { t } = useTranslation('ai-headshot-generator');
  return (
    <section
      className={`${styles.container} ${styles.pricingSection}`}
      id='pricing'
      aria-labelledby='pricing-title'
    >
      <div className={styles.sectionHeading}>
        <div>
          <p className={styles.eyebrow}>
            {t('ONE SESSION. PLENTY OF POSSIBILITIES.')}
          </p>
          <h2 id='pricing-title'>
            {t('A small investment.')}
            <br />
            <em>{t('A lasting impression.')}</em>
          </h2>
        </div>
        <p>
          {t(
            'Pick the package that fits your next move. Every plan is a one-time payment, with no subscription.',
          )}
        </p>
      </div>
      <div className={styles.pricingToolbar}>
        <div
          className={styles.filters}
          role='group'
          aria-label={t('Choose pricing type')}
        >
          <button
            type='button'
            aria-pressed={pricingType === 'individual'}
            onClick={() => setPricingType('individual')}
          >
            {t('Individual')}
          </button>
          <button
            type='button'
            aria-pressed={pricingType === 'team'}
            onClick={() => setPricingType('team')}
          >
            {t('Team')}
          </button>
        </div>
        <span>{t('One-time payment')}</span>
      </div>
      {pricingType === 'individual' ? (
        <div className={styles.plans}>
          {plans.map((plan) => (
            <article
              key={plan.title}
              className={styles.plan}
              data-featured={plan.title === 'Professional'}
            >
              <div className={styles.planName}>
                <h3>{t(plan.title)}</h3>
                {plan.title === 'Professional' && (
                  <span>{t('Most popular')}</span>
                )}
              </div>
              <p className={styles.planPrice}>
                {plan.price}
                <small>USD</small>
              </p>
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}>{t(feature)}</li>
                ))}
              </ul>
              <button
                type='button'
                className={
                  plan.title === 'Professional'
                    ? styles.primaryButton
                    : styles.secondaryButton
                }
                disabled={pendingPrice !== null}
                onClick={() => onPurchase(plan.priceID)}
              >
                {pendingPrice === plan.priceID
                  ? t('Opening checkout…')
                  : t('Choose {{plan}}', { plan: t(plan.title) })}
                <span aria-hidden='true'>↗</span>
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div className={styles.teamPlan}>
          <div>
            <h3>{t('A consistent look for your whole team.')}</h3>
            <p>
              {t(
                'Professional portraits for everyone, wherever they work. Volume discounts start at 10 people.',
              )}
            </p>
            <label htmlFor='studio-team-size'>
              {t('Team size')}
              <strong>{t('{{count}} people', { count: teamSize })}</strong>
            </label>
            <input
              id='studio-team-size'
              type='range'
              min={3}
              max={50}
              value={teamSize}
              onChange={(e) => setTeamSize(Number(e.target.value))}
            />
            <div className={styles.rangeLabels}>
              <span>3</span>
              <span>50</span>
            </div>
          </div>
          <div className={styles.teamTotal}>
            <p className={styles.planPrice} aria-live='polite'>
              ${discountedPrice}
              <small>USD</small>
            </p>
            {discount > 0 && (
              <p>
                <s>${originalPrice}</s> ·{' '}
                {t('Save {{discount}}%', { discount })}
              </p>
            )}
            <p>{t('$29 per person before volume discounts.')}</p>
            <button
              type='button'
              className={styles.primaryButton}
              disabled={pendingPrice !== null}
              onClick={() => onPurchase(plans[1].priceID, teamSize)}
            >
              {pendingPrice
                ? t('Opening checkout…')
                : t('Get Headshots for Your Team')}
              <span aria-hidden='true'>↗</span>
            </button>
            <p>{t('Team size will be fixed at checkout')}</p>
          </div>
        </div>
      )}
      <div className={styles.reviews}>
        <p className={styles.eyebrow}>{t('What our customers say')}</p>
        <div className={styles.reviewGrid}>
          {testimonials.map((review) => (
            <figure key={review.name}>
              <div
                className={styles.rating}
                aria-label={t('{{count}} out of 5 stars', {
                  count: review.stars,
                })}
              >
                <span aria-hidden='true'>
                  {'★'.repeat(review.stars)}
                  {'☆'.repeat(5 - review.stars)}
                </span>
              </div>
              <blockquote>{t(review.quote)}</blockquote>
              <figcaption>
                <span className={styles.initials} aria-hidden='true'>
                  {review.name
                    .split(' ')
                    .map((part) => part[0])
                    .join('')}
                </span>
                <div>
                  <strong>{review.name}</strong>
                  <span>{t(review.title)}</span>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
